<template>
  <ui-popover ref="popover" panel-class="rd-pop--companies" :label="title" @open="query = ''">
    <template #trigger="{ attrs, on }">
      <button
        type="button"
        class="ui-btn"
        :class="{ 'ui-btn--accent': accent }"
        v-bind="attrs"
        :disabled="disabled"
        :data-rd-trigger="name"
        v-on="on"
      >
        <v-icon v-if="icon" size="16" :color="accent ? '#9a3412' : '#5f5852'">{{ icon }}</v-icon>
        <span class="rd-bar-label" :title="label">{{ label }}</span>
        <v-icon size="16" :color="accent ? '#9a3412' : '#5f5852'">mdi-chevron-down</v-icon>
      </button>
    </template>

    <template #default="{ close }">
      <div class="rd-pop__head">
        <span>{{ title }}</span>
        <span v-if="multiple" class="rd-num">
          {{ $t("report_mobile.selected_of", { selected: selectedIds.length, total: items.length }) }}
        </span>
      </div>
      <label v-if="items.length > 7" class="rd-pop__search">
        <v-icon size="16" color="#5f5852">mdi-magnify</v-icon>
        <span class="rd-visually-hidden">{{ searchLabel }}</span>
        <input ref="query" v-model="query" type="search" :placeholder="searchLabel" />
      </label>
      <div class="rd-checklist" role="group" :aria-label="title">
        <label v-for="item in visibleItems" :key="String(item.id)">
          <input
            :type="multiple ? 'checkbox' : 'radio'"
            :name="`rd-pick-${name}`"
            :checked="isSelected(item.id)"
            @change="pick(item.id)"
          />
          <span>{{ toLatin(item.name) }}</span>
        </label>
        <span v-if="!visibleItems.length" class="rd-muted">{{ $t("report_mobile.no_results") }}</span>
      </div>
      <div v-if="multiple" class="rd-pop__links">
        <button type="button" class="ui-text-btn" @click="$emit('input', items.map((item) => item.id))">
          {{ $t("report_mobile.all") }}
        </button>
        <button type="button" class="ui-text-btn" @click="$emit('input', [])">
          {{ $t("report_desktop.none") }}
        </button>
        <button type="button" class="ui-text-btn rd-pop__done" @click="close(true)">
          {{ $t("report_desktop.done") }}
        </button>
      </div>
    </template>
  </ui-popover>
</template>

<script>
/**
 * Izbor iz liste u traci filtera magacina (magacini, dobavljači, status, tip dokumenta…): dugme sa izabranom
 * vrednošću i iskačući spisak sa pretragom. `multiple` daje više izbora (vrednost je niz id-jeva), inače je
 * jedan izbor (vrednost je id; stavka sa id `null` znači "svi"). Ne drži izbor: javlja `input`.
 */
export default {
  name: "WarehousePick",
  props: {
    /** [{ id, name }] */
    items: { type: Array, default: () => [] },
    value: { type: [Array, String, Number], default: null },
    multiple: { type: Boolean, default: false },
    /** naziv u iskačućem izboru i za čitač ekrana */
    title: { type: String, required: true },
    searchLabel: { type: String, default: "" },
    /** tekst kad ništa nije izabrano (višestruki izbor) */
    placeholder: { type: String, default: "" },
    /** tekst kad su izabrani svi (višestruki izbor) */
    allLabel: { type: String, default: "" },
    icon: { type: String, default: "" },
    name: { type: String, default: "pick" },
    disabled: { type: Boolean, default: false },
    /** jedan izbor: vrednost koja se ne ističe bojom (podrazumevana) */
    neutral: { type: [String, Number], default: null },
    /** nikad se ne ističe bojom (izbor koji uvek ima vrednost, npr. vrsta prikaza) */
    plain: { type: Boolean, default: false },
    /** jedan izbor: ispred vrednosti stoji naziv izbora ("Status: Svi"), da se izbori sa istom vrednošću razlikuju */
    labeled: { type: Boolean, default: false },
  },
  data: () => ({
    query: "",
  }),
  computed: {
    selectedIds() {
      if (this.multiple) {
        return Array.isArray(this.value) ? this.value : [];
      }
      return [this.value];
    },
    selectedItems() {
      return this.items.filter((item) => this.selectedIds.includes(item.id));
    },
    allSelected() {
      return this.multiple && this.items.length > 0 && this.selectedItems.length === this.items.length;
    },
    accent() {
      if (this.plain) {
        return false;
      }
      if (this.multiple) {
        return !this.allSelected;
      }
      return this.value !== this.neutral;
    },
    label() {
      if (!this.multiple) {
        const item = this.selectedItems[0];
        if (!item) {
          return this.placeholder || this.title;
        }
        return this.labeled ? `${this.title}: ${this.toLatin(item.name)}` : this.toLatin(item.name);
      }
      if (!this.selectedItems.length) {
        return this.placeholder || this.title;
      }
      if (this.allSelected && this.items.length > 1) {
        return this.allLabel || this.title;
      }
      const first = this.toLatin(this.selectedItems[0].name);
      return this.selectedItems.length === 1 ? first : `${first} +${this.selectedItems.length - 1}`;
    },
    visibleItems() {
      const query = this.query.trim();
      if (!query) {
        return this.items;
      }
      return this.customSearch(
        this.items.map((item) => ({ ...item, text: this.toLatin(item.name) })),
        [{ value: "text" }],
        query
      );
    },
  },
  methods: {
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    pick(id) {
      if (!this.multiple) {
        this.$emit("input", id);
        this.$refs.popover.close(true);
        return;
      }
      this.$emit(
        "input",
        this.isSelected(id) ? this.selectedIds.filter((item) => item !== id) : [...this.selectedIds, id]
      );
    },
  },
};
</script>
